import productApi, { FetchVendorListParam } from "@api/productApi";
import type { VendorListResult } from "@api/productApi";
import type { StandardResponse } from "src/types";
import * as vendorApiAll from "@api/vendorApi";
import { vendorAPI } from "@api/vendorApi";
import type {
  CreateVendorPayload,
  VendorContact,
  VendorContactListParams,
  VendorContactListResult,
  VendorListResponse,
  VendorDetail,
  VendorListItem,
  VendorListParams,
  UpdateVendorPayload,
} from "@page/VendorInfoPage/interface";
import type {
  EditSupplementDetail,
  SupplementDetail,
  SupplementListParams,
  SupplementListResult,
} from "@page/VendorSupplementPage/interface";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import downloadFile from "@utils/downloadFile";
import { message, Modal } from "antd";
import type { RootState } from "./rootReducer";

interface IState {
  isFetchingVendorList: boolean;
  vendorListResult: VendorListResponse;
  vendorListParams: VendorListParams;
  filterVendorListResult: VendorListResult;
  filterVendorListParams: FetchVendorListParam;
  selectedVendorIds: number[];
  isFetchingContactList: boolean;
  vendorContactListResult: VendorContactListResult;
  vendorContactListParams: VendorContactListParams;
  isFetchingSupplementList: boolean;
  supplementListResult: SupplementListResult;
  supplementListParams: SupplementListParams;
  supplementDetail: SupplementDetail;
  marinBalanceListResult?: vendorApiAll.MarginBalanceListResponse;
  marginAddTopList: vendorApiAll.MarginAddTopList[];
  marginAddTopInfo?: vendorApiAll.MarginAddTopList;
  profitsConversionList: vendorApiAll.ProfitsConversionList[];
  profitsConversionInfo?: vendorApiAll.ProfitsConversionList;
  isMarginFetching: boolean;
  isProfitsFetching: boolean;
  salesRecordList: vendorApiAll.VenderSalesRecordItem[];
  salesChannelList?: vendorApiAll.SalesChannelList;

  // 廠商申請單
  isFetching: boolean;
  vendorList: VendorListItem[];
  vendorListTotal: number;
  vendorDetail: VendorDetail | null;
}

const initialState: IState = {
  isFetchingVendorList: false,
  vendorListResult: {
    count: 0,
    previous: "",
    next: "",
    results: [],
  },
  vendorListParams: {
    limit: 20,
    offset: 0,
  },
  filterVendorListResult: {
    count: 0,
    previous: "",
    next: "",
    results: [],
  },
  filterVendorListParams: {
    limit: 20,
    offset: 0,
  },
  selectedVendorIds: [],
  isFetchingContactList: false,
  vendorContactListResult: {
    count: 0,
    previous: "",
    next: "",
    results: [],
  },
  vendorContactListParams: {
    limit: 20,
    offset: 0,
    vendorId: -1,
  },
  isFetchingSupplementList: false,
  supplementListResult: {
    count: 0,
    previous: "",
    next: "",
    results: [],
  },
  supplementListParams: {
    limit: 20,
    offset: 0,
  },
  supplementDetail: {
    id: -1,
    approvalStatus: -1,
    supplementPurpose: -1,
    salesPlanId: -1,
    sku: "",
    extraAmount: 0,
    supplementDate: "",
    staff: { id: -1, name: "" },
    createdAt: "",
    sectionManagerApproval: -1,
    financeApproval: -1,
    vendor: { vendorId: -1, vendorName: "" },
    estimatedSupplementDate: "",
    supplementDescription: "",
    productName: "",
    destroyedAt: "",
    updatedAt: "",
    mode: -1,
  },
  marinBalanceListResult: undefined,
  marginAddTopList: [],
  marginAddTopInfo: undefined,
  profitsConversionList: [],
  profitsConversionInfo: undefined,
  isMarginFetching: false,
  isProfitsFetching: false,
  salesRecordList: [],
  salesChannelList: undefined,
  // 廠商申請單
  isFetching: false,
  vendorList: [],
  vendorListTotal: 0,
  vendorDetail: null,
};

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : "發生未知錯誤");

//  -------------- 廠商申請單相關 ---------------
export const fetchVendorList = createAsyncThunk<VendorListResponse>("vendor/fetchVendorList", async (_, thunkApi) => {
  const {
    vendorSlice: { vendorListParams },
  } = thunkApi.getState() as RootState;
  const response = await vendorAPI.fetchVendorList(vendorListParams);
  return response;
});
export const fetchVendorDetail = createAsyncThunk<VendorDetail, number>("vendor/fetchVendorDetail", async (id) => {
  const response = await vendorAPI.fetchVendorDetail(id);
  return response;
});

export const createVendor = createAsyncThunk<{ id: number }, CreateVendorPayload>(
  "vendor/createVendor",
  async (payload) => {
    const result = await vendorAPI.fetchCreateVendor(payload);
    return { id: result.id };
  },
);

export const updateVendor = createAsyncThunk<VendorDetail, { id: number; payload: UpdateVendorPayload }>(
  "vendor/updateVendor",
  async ({ id, payload }) => {
    const result = await vendorAPI.fetchUpdateVendor(id, payload);
    return result;
  },
);

export const reviewVendorApplication = createAsyncThunk<
  { applicationStatus: number },
  { id: number; isApproved: boolean; rejectReason?: string }
>("vendor/reviewVendorApplication", async ({ id, isApproved, rejectReason }) =>
  vendorAPI.reviewVendorApplication(id, { isApproved, rejectReason }),
);

// -------------- 廠商申請單相關 End ---------------

export const fetchFilterVendorList = createAsyncThunk<VendorListResult, string>(
  "vendor/fetchFilterVendorList",
  async (name, thunkApi) => {
    const params: FetchVendorListParam = {
      name: name || undefined,
      limit: 20,
      offset: 0,
    };

    thunkApi.dispatch(updateFilterVendorListParams(params));
    const response = await productApi.fetchVendorList(params);
    return response;
  },
);

export const loadMoreFilterVendorList = createAsyncThunk<VendorListResult>(
  "vendor/loadMoreFilterVendorList",
  async (_, thunkApi) => {
    const {
      vendorSlice: { filterVendorListParams },
    } = thunkApi.getState() as RootState;
    const params: FetchVendorListParam = {
      ...filterVendorListParams,
      offset: filterVendorListParams.offset + filterVendorListParams.limit,
    };

    thunkApi.dispatch(updateFilterVendorListParams(params));
    const response = await productApi.fetchVendorList(params);
    return response;
  },
);

export const clearVendorFilter = createAsyncThunk("vendor/clearVendorFilter", async (_, thunkApi) => {
  thunkApi.dispatch(updateVendorListParams(initialState.vendorListParams));
  thunkApi.dispatch(fetchVendorList());
});

export const updateOwner = createAsyncThunk<void, number>("vendor/updateOwner", async (ownerId, thunkApi) => {
  const {
    vendorSlice: { selectedVendorIds },
  } = thunkApi.getState() as RootState;

  await vendorApiAll.fetchOwnerUpdate({ vendorIds: selectedVendorIds, ownerId });
  thunkApi.dispatch(fetchVendorList());
});

export const modifyVendorCooperation = createAsyncThunk<void, { vendorId: number; isActive: boolean }>(
  "vendor/modifyVendorCooperation",
  async (params, thunkApi) => {
    await vendorApiAll.fetchVendorManagement(params);
    thunkApi.dispatch(fetchVendorList());
  },
);

export const fetchVendorContactList = createAsyncThunk<VendorContactListResult>(
  "vendor/fetchVendorContactList",
  async (_, thunkApi) => {
    const {
      vendorSlice: { vendorContactListParams },
    } = thunkApi.getState() as RootState;

    const response = await vendorApiAll.fetchVendorContactList(vendorContactListParams);
    return response;
  },
);

export const deleteContact = createAsyncThunk<StandardResponse, number>(
  "vendor/deleteContact",
  async (contactId, thunkApi) => {
    try {
      const response = await vendorApiAll.fetchDeletedVendorContact(contactId);
      thunkApi.dispatch(fetchVendorContactList());
      return response;
    } catch (error: any) {
      message.error(`無法刪除聯絡人 ${error.message}`);
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const addContact = createAsyncThunk<VendorContact, { contactInfo: VendorContact; vendorId: number }>(
  "vendor/addContact",
  async (params, thunkApi) => {
    const { contactInfo, vendorId } = params;
    try {
      const response = await vendorApiAll.fetchNewVendorContact(contactInfo, vendorId);
      thunkApi.dispatch(fetchVendorContactList());
      message.success("新增聯絡人成功");
      return response;
    } catch (error: any) {
      message.error(`無法新建聯絡人 ${error.message}`);
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const updateContact = createAsyncThunk<any, VendorContact>(
  "vendor/updateContact",
  async (contactInfo, thunkApi) => {
    const { id } = contactInfo;
    try {
      const response = await vendorApiAll.fetchModifiedVendorContact(contactInfo, id);
      thunkApi.dispatch(fetchVendorContactList());
      message.success("編輯聯絡人成功");
      return response;
    } catch (error: any) {
      message.error(`無法編輯聯絡人 ${error.message}`);
      return thunkApi.rejectWithValue(error);
    }
  },
);
// -------------- 補單 ---------------
export const fetchSupplementList = createAsyncThunk<SupplementListResult>(
  "vendor/fetchSupplementList",
  async (_, thunkApi) => {
    const {
      vendorSlice: { supplementListParams },
    } = thunkApi.getState() as RootState;

    const response = await vendorApiAll.fetchSupplementList(supplementListParams);
    return response;
  },
);

export const fetchSupplementDetail = createAsyncThunk("vendor/fetchSupplementDetail", async (id: number) => {
  const response = await vendorApiAll.fetchSupplementDetail(id);
  return response;
});

export const addSupplement = createAsyncThunk(
  "vendor/addSupplement",
  async (payload: Partial<EditSupplementDetail>, thunkApi) => {
    try {
      const response = await vendorApiAll.addSupplement(payload);
      message.success("新增成功");
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(getErrorMessage(error));
    }
  },
);

export const reviewSupplement = createAsyncThunk("vendor/reviewSupplement", async (isApproved: boolean, thunkApi) => {
  const {
    vendorSlice: { supplementDetail },
  } = thunkApi.getState() as RootState;
  const response = await vendorApiAll.approveSupplement(supplementDetail.id, { isApproved });

  thunkApi.dispatch(fetchSupplementDetail(supplementDetail.id));
  return response;
});

export const editSupplement = createAsyncThunk(
  "vendor/editSupplement",
  async (payload: Partial<EditSupplementDetail>, thunkApi) => {
    const {
      vendorSlice: { supplementDetail },
    } = thunkApi.getState() as RootState;
    const response = await vendorApiAll.editSupplement(supplementDetail.id, payload);

    thunkApi.dispatch(fetchSupplementDetail(supplementDetail.id));

    return response;
  },
);

export const deleteSupplement = createAsyncThunk("vendor/deleteSupplement", async (id: number, thunkApi) => {
  await new Promise((res) => {
    Modal.confirm({
      title: "警告",
      content: "你確定要作廢月結補單",
      okText: "確認",
      cancelText: "取消",
      onOk: () => {
        res(null);
        return false;
      },
    });
  });

  const response = await vendorApiAll.deleteSupplement(id);

  thunkApi.dispatch(fetchSupplementList());
  return response;
});

export const exportSupplement = createAsyncThunk("vendor/exportSupplement", async (_, thunkApi) => {
  const {
    vendorSlice: { supplementListParams },
  } = thunkApi.getState() as RootState;

  const response = await vendorApiAll.exportSupplement(supplementListParams);
  return response;
});

export const fetchSupplementVenderRelation = createAsyncThunk<
  { sku: string; modes: number[]; salesChannel: { id: number; modes: number[] }[] },
  { sku: string; vendor: number }
>("vendor/fetchSupplementVenderRelation", async (params, _) => {
  const response = await vendorApiAll.fetchSupplementVenderRelation(params);
  return response;
});

// 保證金餘額列表
export const fetchMarginBalanceList = createAsyncThunk("vendor/fetchMarginBalanceList", async (vendorId?: number) => {
  const response = await vendorApiAll.fetchMarginBalanceList(vendorId);
  return response;
});

// 保證金相關
export const fetchMarginSendReceipt = createAsyncThunk(
  "vendor/fetchMarginSendReceipt",
  async (config: { vendorId: number; addTopId: number }, thunkApi) => {
    try {
      const response = await vendorApiAll.fetchMarginSendReceipt(config.vendorId, config.addTopId);
      const fileName = response.split("Receipts_");
      downloadFile(response, `Receipts_${fileName[1]}`);
      await thunkApi.dispatch(fetchMarginAddTopList(config.vendorId));
      message.success("開立成功");
      return "Success";
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

export const fetchDeleteMarginAddTop = createAsyncThunk(
  "vendor/fetchDeleteMarginAddTop",
  async (config: { vendorId: number; addTopId: number }, thunkApi) => {
    await vendorApiAll.fetchDeleteMarginAddTop(config.vendorId, config.addTopId);
    await thunkApi.dispatch(fetchMarginAddTopList(config.vendorId));
    return "Success";
  },
);

export const fetchMarginAddTopList = createAsyncThunk("vendor/fetchMarginAddTopList", async (vendorId: number) => {
  const response = await vendorApiAll.fetchMarginAddTopList(vendorId);
  return response;
});

export const fetchMarginAddTopInfo = createAsyncThunk(
  "vendor/fetchMarginAddTopInfo",
  async (config: { vendorId: number; addTopId: number }) => {
    const response = await vendorApiAll.fetchMarginAddTopInfo(config.vendorId, config.addTopId);
    return response;
  },
);

export const fetchCreateMarginAddTop = createAsyncThunk(
  "vendor/fetchCreateMarginAddTop",
  async (config: { vendorId: number; payload: vendorApiAll.CreateMarginAddTopPayload }, thunkApi) => {
    try {
      const response = await vendorApiAll.fetchCreateMarginAddTop(config.vendorId, config.payload);
      await thunkApi.dispatch(fetchMarginAddTopList(config.vendorId));
      message.success("建立成功");
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

export const fetchEditMarginAddTop = createAsyncThunk(
  "vendor/fetchEditMarginAddTop",
  async (config: { vendorId: number; addTopId: number; payload: vendorApiAll.EditMarginAddTopPayload }, thunkApi) => {
    try {
      const response = await vendorApiAll.fetchEditMarginAddTop(config.vendorId, config.addTopId, config.payload);
      await thunkApi.dispatch(fetchMarginAddTopList(config.vendorId));
      message.success("更新成功");
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

// 利潤轉換
export const fetchProfitsConversionList = createAsyncThunk(
  "vendor/fetchProfitsConversionList",
  async (vendorId: number) => {
    const response = await vendorApiAll.fetchProfitsConversionList(vendorId);
    return response;
  },
);

export const fetchProfitsConversionInfo = createAsyncThunk(
  "vendor/fetchProfitsConversionInfo",
  async (config: { vendorId: number; profitsId: number }) => {
    const response = await vendorApiAll.fetchProfitsConversionInfo(config.vendorId, config.profitsId);
    return response;
  },
);

export const fetchCreateTransferProfits = createAsyncThunk(
  "vendor/fetchCreateTransferProfits",
  async (config: { vendorId: number; payload: vendorApiAll.CreateTransferProfitsPayload }, thunkApi) => {
    try {
      const response = await vendorApiAll.fetchCreateTransferProfits(config.vendorId, config.payload);
      await thunkApi.dispatch(fetchProfitsConversionList(config.vendorId));
      message.success("建立成功");
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

export const fetchEditTransferProfits = createAsyncThunk(
  "vendor/fetchEditTransferProfits",
  async (
    config: { vendorId: number; profitsId: number; payload: vendorApiAll.EditTransferProfitsPayload },
    thunkApi,
  ) => {
    try {
      const response = await vendorApiAll.fetchEditTransferProfits(config.vendorId, config.profitsId, config.payload);
      await thunkApi.dispatch(fetchProfitsConversionList(config.vendorId));
      await thunkApi.dispatch(fetchProfitsConversionInfo(config));
      message.success("更新成功");
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

export const fetchDeleteTransferProfits = createAsyncThunk(
  "vendor/fetchDeleteTransferProfits",
  async (config: { vendorId: number; profitsId: number }, thunkApi) => {
    try {
      const response = await vendorApiAll.fetchDeleteTransferProfits(config.vendorId, config.profitsId);
      await thunkApi.dispatch(fetchProfitsConversionInfo(config));
      message.success("審核成功");
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

export const fetchApproveTransferProfits = createAsyncThunk(
  "vendor/fetchApproveTransferProfits",
  async (
    config: { vendorId: number; profitsId: number; payload: vendorApiAll.ApproveTransferProfitsPayload },
    thunkApi,
  ) => {
    try {
      const response = await vendorApiAll.fetchApproveTransferProfits(
        config.vendorId,
        config.profitsId,
        config.payload,
      );
      await thunkApi.dispatch(fetchProfitsConversionInfo(config));
      message.success("審核成功");
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

export const fetchMarginSendInvoice = createAsyncThunk(
  "vendor/fetchMarginSendInvoice",
  async (config: { vendorId: number; profitsId: number }, thunkApi) => {
    try {
      await vendorApiAll.fetchMarginSendInvoice(config.vendorId, config.profitsId);
      await thunkApi.dispatch(fetchProfitsConversionList(config.vendorId));
      message.success("開立成功");
      return "Success";
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);

export const fetchVenderSalesRecordList = createAsyncThunk(
  "vendor/fetchVenderSalesRecordList",
  async (vendorId: number, thunkApi) => {
    try {
      const response = await vendorApiAll.fetchVenderSalesRecordList(vendorId);
      return response;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);
export const addVenderSalesRecord = createAsyncThunk(
  "vendor/addVenderSalesRecord",
  async (config: { vendorId: number; addItem: vendorApiAll.AddSalesRecordItem }, thunkApi) => {
    const { vendorId, addItem } = config;
    try {
      await vendorApiAll.addVenderSalesRecord(vendorId, addItem);
      await thunkApi.dispatch(fetchVenderSalesRecordList(vendorId));
      message.success("新增成功");
      return "Success";
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);
export const deleteVenderSalesRecord = createAsyncThunk(
  "vendor/deleteVenderSalesRecord",
  async (config: { vendorId: number; recordId: number }, thunkApi) => {
    const { vendorId, recordId } = config;
    try {
      await vendorApiAll.deleteVenderSalesRecord(vendorId, recordId);
      await thunkApi.dispatch(fetchVenderSalesRecordList(vendorId));
      message.success("刪除成功");
      return "Success";
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);
export const createVendorSalesInvoice = createAsyncThunk(
  "vendor/createVendorSalesInvoice",
  async (config: { vendorId: number; recordId: number }, thunkApi) => {
    const { vendorId, recordId } = config;
    try {
      await vendorApiAll.createVendorSalesInvoice(vendorId, recordId);
      await thunkApi.dispatch(fetchVenderSalesRecordList(vendorId));
      message.success("開立成功");
      return "Success";
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.message);
    }
  },
);
// 取得通路列表
export const fetchSalesChannelList = createAsyncThunk("vendor/fetchSalesChannelList", async (_, thunkApi) => {
  try {
    const response = await vendorApiAll.fetchSalesChannelList();
    return response;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error.message);
  }
});

const vendorSlice = createSlice({
  name: "vendor",
  initialState,
  reducers: {
    resetVendorSlice: () => initialState,
    resetFilterVendorListResult: (state) => {
      state.filterVendorListResult = initialState.filterVendorListResult;
    },
    updateVendorListParams: (state, action: PayloadAction<VendorListParams>) => {
      state.vendorListParams = action.payload;
    },
    updateFilterVendorListParams: (state, action: PayloadAction<FetchVendorListParam>) => {
      state.filterVendorListParams = action.payload;
    },
    updateSelectedVendorIds: (state, action: PayloadAction<number[]>) => {
      state.selectedVendorIds = action.payload;
    },
    updateContactListParams: (state, action: PayloadAction<VendorContactListParams>) => {
      state.vendorContactListParams = action.payload;
    },
    updateVendorSupplementParams: (state, action: PayloadAction<SupplementListParams>) => {
      state.supplementListParams = action.payload;
    },
    clearVendorSupplementParams: (state) => {
      state.supplementListParams = initialState.supplementListParams;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchVendorList.pending, (state) => {
      state.isFetchingVendorList = true;
    });
    builder.addCase(fetchVendorList.fulfilled, (state, action) => {
      state.isFetchingVendorList = false;
      state.vendorListResult = action.payload;
      state.vendorList = action.payload.results;
      state.vendorListTotal = action.payload.count;
    });
    builder.addCase(fetchVendorList.rejected, (state) => {
      state.isFetchingVendorList = false;
    });

    builder.addCase(fetchFilterVendorList.fulfilled, (state, action) => {
      state.filterVendorListResult = action.payload;
    });
    builder.addCase(loadMoreFilterVendorList.fulfilled, (state, action) => {
      state.filterVendorListResult = {
        ...action.payload,
        results: state.filterVendorListResult.results.concat(action.payload.results),
      };
    });
    builder.addCase(fetchVendorContactList.pending, (state) => {
      state.isFetchingContactList = true;
    });
    builder.addCase(fetchVendorContactList.fulfilled, (state, action) => {
      state.isFetchingContactList = false;
      state.vendorContactListResult = action.payload;
    });

    builder.addCase(fetchSupplementList.pending, (state) => {
      state.isFetchingSupplementList = true;
    });
    builder.addCase(fetchSupplementList.fulfilled, (state, action) => {
      state.isFetchingSupplementList = false;
      state.supplementListResult = action.payload;
    });

    builder.addCase(fetchSupplementDetail.fulfilled, (state, action) => {
      state.supplementDetail = action.payload;
    });
    builder.addCase(addSupplement.rejected, (state, action) => {
      message.error(action?.error?.message ?? "未預期的錯誤");
    });

    builder.addCase(addSupplement.fulfilled, (state, action) => {
      window.location.href = `/supplement/${action.payload.result.id}`;
    });
    builder.addCase(reviewSupplement.rejected, (state, action) => {
      message.error(action?.error?.message ?? "未預期的錯誤");
    });

    builder.addCase(fetchMarginBalanceList.fulfilled, (state, action) => {
      state.marinBalanceListResult = action.payload;
    });
    builder.addCase(fetchMarginAddTopList.pending, (state) => {
      state.isMarginFetching = true;
    });
    builder.addCase(fetchMarginAddTopList.fulfilled, (state, action) => {
      state.marginAddTopList = action.payload;
      state.isMarginFetching = false;
    });
    builder.addCase(fetchProfitsConversionList.pending, (state) => {
      state.isProfitsFetching = true;
    });
    builder.addCase(fetchProfitsConversionList.fulfilled, (state, action) => {
      state.profitsConversionList = action.payload;
      state.isProfitsFetching = false;
    });
    builder.addCase(fetchMarginAddTopInfo.fulfilled, (state, action) => {
      state.marginAddTopInfo = action.payload;
    });
    builder.addCase(fetchProfitsConversionInfo.fulfilled, (state, action) => {
      state.profitsConversionInfo = action.payload;
    });
    builder.addCase(fetchMarginSendReceipt.pending, (state) => {
      state.isMarginFetching = true;
    });
    builder.addCase(fetchMarginSendReceipt.fulfilled, (state) => {
      state.isMarginFetching = false;
    });
    builder.addCase(fetchMarginSendReceipt.rejected, (state) => {
      state.isMarginFetching = false;
    });
    builder.addCase(fetchVenderSalesRecordList.pending, (state) => {
      state.isFetchingVendorList = true;
    });
    builder.addCase(fetchVenderSalesRecordList.fulfilled, (state, action) => {
      state.isFetchingVendorList = false;
      state.salesRecordList = action.payload;
    });
    builder.addCase(fetchVenderSalesRecordList.rejected, (state) => {
      state.isFetchingVendorList = false;
    });
    builder.addCase(fetchSalesChannelList.fulfilled, (state, action) => {
      state.salesChannelList = action.payload;
    });

    builder.addCase(fetchVendorDetail.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchVendorDetail.fulfilled, (state, action) => {
      state.isFetching = false;
      state.vendorDetail = action.payload;
    });
    builder.addCase(fetchVendorDetail.rejected, (state) => {
      state.isFetching = false;
    });

    builder.addCase(createVendor.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(createVendor.fulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addCase(createVendor.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(updateVendor.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(updateVendor.fulfilled, (state, action) => {
      state.isFetching = false;
      state.vendorDetail = action.payload;
    });
    builder.addCase(updateVendor.rejected, (state) => {
      state.isFetching = false;
    });

    builder.addCase(reviewVendorApplication.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(reviewVendorApplication.fulfilled, (state, action) => {
      state.isFetching = false;
      if (state.vendorDetail) {
        state.vendorDetail.applicationStatus = action.payload.applicationStatus;
      }
    });
    builder.addCase(reviewVendorApplication.rejected, (state) => {
      state.isFetching = false;
    });
  },
});

export const {
  resetVendorSlice,
  updateVendorListParams,
  updateFilterVendorListParams,
  updateContactListParams,
  updateSelectedVendorIds,
  clearVendorSupplementParams,
  updateVendorSupplementParams,
  resetFilterVendorListResult,
} = vendorSlice.actions;

export const vendorState = (root: RootState) => root.vendorSlice;
export default vendorSlice.reducer;
